import { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { clientesDosDados, formatarNumero, formatarPercentualCurto, passaNoFiltro, rotuloCurtoMes, temaGrafico, temFiltro } from "./analiseUtils";
import { derivados, fichasDaCombinacao, formatarMoeda, somarLinhas } from "./acordosUtils";

export const DIMENSOES = [
    { value: "negociador", label: "Negociador" },
    { value: "coordenador", label: "Coordenador" },
    { value: "cliente", label: "Cliente" },
    { value: "mes", label: "Mês" },
];
const ROTULO_DIMENSAO = Object.fromEntries(DIMENSOES.map(d => [d.value, d.label]));

const COLUNAS = [
    { id: "fichasAcionadas", rotulo: "Fichas acionadas", formato: "numero", ajuda: "Fichas acionadas (mesma conta da análise de atendimento). No mês: fichas acionadas no mês." },
    { id: "acordos", rotulo: "Acordos", formato: "numero", ajuda: "Acordos formalizados no período." },
    { id: "conversao", rotulo: "Conversão", formato: "pct", ajuda: "Fichas acionadas que tiveram acordo de quem as acionou ÷ fichas acionadas. No mês: fichas com o 1º acordo no mês ÷ fichas acionadas no mês." },
    { id: "valorAcordos", rotulo: "Negociado", formato: "moeda", ajuda: "Soma do valor dos acordos formalizados no período." },
    { id: "ticketMedio", rotulo: "Ticket médio", formato: "moeda", ajuda: "Valor negociado ÷ acordos." },
    { id: "descontoPct", rotulo: "Desconto", formato: "pct", ajuda: "Desconto concedido ÷ valor antes do desconto." },
    { id: "quebra", rotulo: "Cancelados", formato: "pct", ajuda: "Acordos formalizados no período que já foram cancelados (inclui os refeitos)." },
    { id: "recebido", rotulo: "Recebido", formato: "moeda", ajuda: "Pagamentos no período: parcelas de acordo e baixa avulsa (sem acordo)." },
    { id: "adimplencia", rotulo: "Adimplência", formato: "pct", ajuda: "Valor pago das parcelas que venceram no período ÷ valor dessas parcelas." },
];

const formatar = (formato, valor) => {
    if (valor === null || valor === undefined) return "–";
    if (formato === "moeda") return formatarMoeda(valor);
    if (formato === "pct") return formatarPercentualCurto(valor);
    return formatarNumero(valor);
};

const valorNaLinha = (linha, dimensao) => (dimensao === "mes" ? linha.mes : linha[dimensao]);

/** Compara dois valores de uma dimensão: mês em ordem cronológica, o resto em ordem alfabética. */
const compararDimensao = (dimensao, a, b) => (dimensao === "mes" ? a - b : String(a ?? "").localeCompare(String(b ?? "")));

/**
 * Chaves (combinações das dimensões escolhidas) de quem só acionou fichas, sem acordo, pagamento ou parcela: entram
 * com zero. Sem como saber (negociador e coordenador juntos, ou um escolhido no filtro e o outro na tabela), nenhuma.
 */
function chavesSoAcionamento(atendimento, dimensoes, filtros) {
    if (!atendimento) return [];
    const pessoaDim = dimensoes.find(d => d === "negociador" || d === "coordenador");
    if (dimensoes.includes("negociador") && dimensoes.includes("coordenador")) return [];
    if ((pessoaDim === "negociador" && temFiltro(filtros.coordenador)) || (pessoaDim === "coordenador" && temFiltro(filtros.negociador))) return [];
    const pessoaFiltro = temFiltro(filtros.negociador) ? filtros.negociador : filtros.coordenador;
    const listaFiltro = temFiltro(filtros.negociador) ? "negociadores" : "coordenadores";
    const linhasDe = (b) => b.linhas.map(l => ({ cliente: l.cliente, fichasMes: l.quantidades || [], fichas: l.fichasPeriodo || 0 }));
    let fontes;
    if (pessoaDim) {
        fontes = (atendimento[pessoaDim === "negociador" ? "negociadores" : "coordenadores"] || [])
            .filter(b => passaNoFiltro(pessoaFiltro, b.nome))
            .map(b => ({ pessoa: b.nome, linhas: linhasDe(b) }));
    } else if (temFiltro(pessoaFiltro)) {
        fontes = (atendimento[listaFiltro] || [])
            .filter(b => passaNoFiltro(pessoaFiltro, b.nome))
            .map(b => ({ pessoa: null, linhas: linhasDe(b) }));
    } else {
        fontes = [{ pessoa: null, linhas: (atendimento.clientes || []).map(c => ({ cliente: c.cliente, fichasMes: c.fichasMes || [], fichas: c.fichasPeriodo || 0 })) }];
    }
    const chaves = [];
    fontes.forEach(fonte => fonte.linhas
        .filter(l => passaNoFiltro(filtros.cliente, l.cliente))
        .forEach(l => {
            const meses = dimensoes.includes("mes") ? l.fichasMes.map((q, i) => (q > 0 ? i : null)).filter(i => i !== null) : (l.fichas > 0 ? [null] : []);
            meses.forEach(mes => chaves.push(dimensoes.map(d => (d === "mes" ? mes : d === "cliente" ? l.cliente : fonte.pessoa))));
        }));
    return chaves;
}

/**
 * Resultado por negociador, coordenador, cliente e/ou mês: um ou mais agrupamentos, na ordem em que foram escolhidos
 * (ex.: negociador e cliente, com os meses). Com mais de um, as linhas ficam agrupadas pelo primeiro, com o total de cada
 * grupo, e o resto em ordem (nomes em ordem alfabética, meses na ordem do período). Quem acionou fichas sem acordo
 * também aparece (conversão 0%).
 */
export default function TabelaAcordos({ linhas, meses, atendimento, cliente, negociador, coordenador, dimensaoInicial }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const [dimensoes, setDimensoes] = useState([dimensaoInicial || "negociador"]);
    const [ordem, setOrdem] = useState({ campo: "recebido", direcao: "desc" });
    const filtros = useMemo(() => ({ cliente, negociador, coordenador }), [cliente, negociador, coordenador]);
    const agrupado = dimensoes.length > 1;

    const rotuloValor = (dimensao, valor) => (dimensao === "mes" ? rotuloCurtoMes(meses[valor]) : valor);

    /** Medidas de um conjunto de linhas + derivados com as fichas da combinação. */
    const medir = (conjunto, combinacao) => {
        const t = somarLinhas(conjunto);
        return { ...t, ...derivados(t, fichasDaCombinacao(atendimento, combinacao, filtros)) };
    };

    const linhasTabela = useMemo(() => {
        const porChave = new Map();
        const chaveDe = (valores) => JSON.stringify(valores);
        linhas.forEach(l => {
            const valores = dimensoes.map(d => valorNaLinha(l, d));
            const k = chaveDe(valores);
            if (!porChave.has(k)) porChave.set(k, { valores, linhas: [] });
            porChave.get(k).linhas.push(l);
        });
        chavesSoAcionamento(atendimento, dimensoes, filtros).forEach(valores => {
            const k = chaveDe(valores);
            if (!porChave.has(k)) porChave.set(k, { valores, linhas: [] });
        });
        // Só cliente ou só mês: todos aparecem, mesmo zerados (como antes)
        if (dimensoes.length === 1 && dimensoes[0] === "cliente") {
            clientesDosDados(atendimento, linhas).filter(c => passaNoFiltro(cliente, c)).forEach(c => {
                const k = chaveDe([c]);
                if (!porChave.has(k)) porChave.set(k, { valores: [c], linhas: [] });
            });
        }
        if (dimensoes.length === 1 && dimensoes[0] === "mes") {
            meses.forEach((m, i) => {
                const k = chaveDe([i]);
                if (!porChave.has(k)) porChave.set(k, { valores: [i], linhas: [] });
            });
        }
        const todosAparecem = dimensoes.length === 1 && (dimensoes[0] === "cliente" || dimensoes[0] === "mes");
        return [...porChave.values()]
            .map(g => {
                const combinacao = Object.fromEntries(dimensoes.map((d, i) => [d, g.valores[i]]));
                return { valores: g.valores, ...medir(g.linhas, combinacao) };
            })
            .filter(g => todosAparecem || g.acordos || g.recebido || g.parcelasVencidas || g.parcelasAVencer || g.fichasAcionadas);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linhas, meses, atendimento, filtros, dimensoes]);

    const comparar = (a, b) => {
        const { campo, direcao } = ordem;
        const sinal = direcao === "asc" ? 1 : -1;
        if (campo === "nome") return sinal * compararDimensao(dimensoes[0], a.valores[0], b.valores[0]);
        return sinal * ((a[campo] ?? -Infinity) - (b[campo] ?? -Infinity)) || compararDimensao(dimensoes[0], a.valores[0], b.valores[0]);
    };

    // Uma dimensão: lista ordenada. Mais de uma: grupos pelo 1º agrupamento (ordenados pela medida escolhida, com o total
    // do grupo) e, dentro de cada um, os demais agrupamentos em ordem
    const blocos = useMemo(() => {
        if (!agrupado) {
            const ordenadas = dimensoes[0] === "mes" && ordem.campo === "nome"
                ? [...linhasTabela].sort((a, b) => (ordem.direcao === "asc" ? 1 : -1) * (a.valores[0] - b.valores[0]))
                : [...linhasTabela].sort(comparar);
            return [{ itens: ordenadas, subtotal: null }];
        }
        const grupos = new Map();
        linhasTabela.forEach(l => {
            const k = JSON.stringify(l.valores[0]);
            if (!grupos.has(k)) grupos.set(k, { valor: l.valores[0], itens: [] });
            grupos.get(k).itens.push(l);
        });
        const internos = dimensoes.slice(1);
        return [...grupos.values()]
            .map(g => {
                const conjunto = linhas.filter(l => valorNaLinha(l, dimensoes[0]) === g.valor);
                return {
                    itens: g.itens.sort((a, b) => {
                        for (let i = 0; i < internos.length; i++) {
                            const c = compararDimensao(internos[i], a.valores[i + 1], b.valores[i + 1]);
                            if (c) return c;
                        }
                        return 0;
                    }),
                    subtotal: { valores: [g.valor], ...medir(conjunto, { [dimensoes[0]]: g.valor }) },
                };
            })
            .sort((a, b) => comparar(a.subtotal, b.subtotal));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linhasTabela, ordem, agrupado, dimensoes, linhas]);

    const total = useMemo(() => ({ valores: [], ...medir(linhas, {}) }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [linhas, atendimento, filtros]);

    const alternarDimensao = (valor) => {
        let novas;
        if (dimensoes.includes(valor)) {
            novas = dimensoes.length > 1 ? dimensoes.filter(d => d !== valor) : dimensoes;
        } else {
            novas = [...dimensoes, valor];
        }
        setDimensoes(novas);
        // Só por mês: na ordem do período, como antes
        if (novas.length === 1 && novas[0] === "mes") setOrdem({ campo: "nome", direcao: "asc" });
    };

    const ordenarPor = (campo) => setOrdem(atual => ({
        campo,
        direcao: atual.campo === campo && atual.direcao === "desc" ? "asc" : campo === "nome" ? "asc" : "desc",
    }));

    const celula = {
        display: "table-cell",
        py: 0.75,
        px: 1.25,
        fontSize: "0.8rem",
        color: `${tema.texto} !important`,
        borderBottom: `1px solid ${tema.borda}`,
        verticalAlign: "top",
        whiteSpace: "nowrap",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${tema.textoSecundario} !important`, fontSize: "0.75rem" };
    // O rótulo ordenado (ativo) do MUI usa a cor de texto do tema claro: no escuro ficava preto
    const rotuloOrdenavel = {
        color: "inherit !important",
        "&.Mui-active": { color: `${tema.texto} !important` },
        "& .MuiTableSortLabel-icon": { color: "inherit !important" },
    };
    const textoMenor = { display: "block", fontSize: "0.7rem", fontWeight: 400, opacity: 0.8, color: "inherit" };
    const fundoSubtotal = darkMode ? "rgba(255, 255, 255, 0.04)" : "#f7f8fa";
    const fundoTotal = darkMode ? "rgba(255, 255, 255, 0.08)" : "#f0f2f5";

    const celulasMedidas = (linha, negrito) => COLUNAS.map(col => (
        <TableCell key={col.id} align="right" sx={{ ...celula, fontWeight: negrito || col.id === "recebido" ? "bold" : undefined }}>
            {formatar(col.formato, linha[col.id])}
            {col.id === "recebido" && linha.recebidoAvulso > 0 && (
                <MDBox component="span" sx={textoMenor}>{formatarMoeda(linha.recebidoAvulso)} avulsa</MDBox>
            )}
            {col.id === "adimplencia" && linha.parcelasVencidas > 0 && (
                <MDBox component="span" sx={textoMenor}>
                    {formatarNumero(linha.vencidasPagas)} de {formatarNumero(linha.parcelasVencidas)} parc.
                </MDBox>
            )}
        </TableCell>
    ));

    const renderLinha = (linha, anterior, chave) => (
        <TableRow key={chave} sx={{ display: "table-row" }}>
            {dimensoes.map((d, i) => {
                // Repete o rótulo só quando muda (como a tabela dinâmica tabular): fica fácil ler o grupo
                const igual = anterior && linha.valores.slice(0, i + 1).every((v, j) => v === anterior.valores[j]);
                return (
                    <TableCell key={d} sx={{ ...celula, fontWeight: i === 0 ? "bold" : undefined }}>
                        {igual ? "" : rotuloValor(d, linha.valores[i])}
                    </TableCell>
                );
            })}
            {celulasMedidas(linha, false)}
        </TableRow>
    );

    const renderSubtotal = (subtotal) => (
        <TableRow key={`subtotal-${JSON.stringify(subtotal.valores[0])}`} sx={{ display: "table-row", background: fundoSubtotal }}>
            <TableCell colSpan={dimensoes.length} sx={{ ...celula, fontWeight: "bold" }}>
                Total {rotuloValor(dimensoes[0], subtotal.valores[0])}
            </TableCell>
            {celulasMedidas(subtotal, true)}
        </TableRow>
    );

    return (
        <MDBox>
            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                {DIMENSOES.map(d => {
                    const posicao = dimensoes.indexOf(d.value);
                    const ativo = posicao >= 0;
                    return (
                        <MDButton
                            key={d.value}
                            size="small"
                            color="info"
                            variant={ativo ? "contained" : "outlined"}
                            onClick={() => alternarDimensao(d.value)}
                            aria-pressed={ativo}
                        >
                            {agrupado && ativo ? `${posicao + 1}. ` : ""}Por {d.label.toLowerCase()}
                        </MDButton>
                    );
                })}
            </MDBox>
            <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5, px: 0.5 }}>
                Escolha um ou mais agrupamentos, na ordem em que quer ver (ex.: negociador, cliente e mês). Clique de novo para tirar.
            </MDTypography>
            <TableContainer sx={{ boxShadow: "none", background: "transparent", borderRadius: 0 }}>
                <Table size="small" sx={{ display: "table", minWidth: 980 }}>
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow sx={{ display: "table-row" }}>
                            {dimensoes.map((d, i) => (
                                <TableCell key={d} sx={cabecalho}>
                                    {i === 0 ? (
                                        <TableSortLabel
                                            active={ordem.campo === "nome"}
                                            direction={ordem.campo === "nome" ? ordem.direcao : "asc"}
                                            onClick={() => ordenarPor("nome")}
                                            sx={rotuloOrdenavel}
                                        >
                                            {ROTULO_DIMENSAO[d]}
                                        </TableSortLabel>
                                    ) : ROTULO_DIMENSAO[d]}
                                </TableCell>
                            ))}
                            {COLUNAS.map(col => (
                                <TableCell key={col.id} align="right" sx={cabecalho}>
                                    <Tooltip title={col.ajuda} placement="top">
                                        <TableSortLabel
                                            active={ordem.campo === col.id}
                                            direction={ordem.campo === col.id ? ordem.direcao : "desc"}
                                            onClick={() => ordenarPor(col.id)}
                                            sx={rotuloOrdenavel}
                                        >
                                            {col.rotulo}
                                        </TableSortLabel>
                                    </Tooltip>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ display: "table-row-group" }}>
                        {blocos.map((bloco, b) => [
                            ...bloco.itens.map((l, i) => renderLinha(l, i > 0 ? bloco.itens[i - 1] : null, `${b}-${JSON.stringify(l.valores)}`)),
                            bloco.subtotal ? renderSubtotal(bloco.subtotal) : null,
                        ])}
                        <TableRow sx={{ display: "table-row", background: fundoTotal }}>
                            <TableCell colSpan={dimensoes.length} sx={{ ...celula, fontWeight: "bold" }}>Total</TableCell>
                            {celulasMedidas(total, true)}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </MDBox>
    );
}
